import React from "react";
import {
  RefreshCw, Sparkles, Brain, Activity, MessageSquare, Heart,
  Target, Lightbulb, CheckCircle2, ArrowRight, AlertTriangle, FileText
} from "lucide-react";
import { Card, CardContent } from "../ui/CCard";
import ManualReviewBadge from "../ui/ManualReviewBadge";
import ConfidenceIndicator from "../ui/ConfidenceIndicator";

const SHOW_AI_FEEDBACK = false; // "What Went Well" AI feedback hidden for now (2026-10-24); data is still stored

// One field of the clinical note. With `name` + `onChange` it's an editable
// box (the clinician's own note stays editable until signed); otherwise a
// read-only card, as for the AI's feedback.
function InsightCard({ icon: Icon, label, value, span = false, name, rawValue, onChange, placeholder }) {
  return (
    <div className={span ? "md:col-span-2" : ""}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />
        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">{label}</h4>
      </div>
      {name && onChange ? (
        <textarea id={`note_${name}`} name={name} value={rawValue ?? ""} onChange={onChange} placeholder={placeholder} aria-label={label}
          className="w-full rounded-md border border-gray-300 p-2 text-sm bg-white min-h-[140px] focus:outline-none focus:ring-2 focus:ring-primary-200" />
      ) : value && value.trim() ? (
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{value}</p>
      ) : (
        <p className="text-xs text-gray-400 italic">Not yet available</p>
      )}
    </div>
  );
}

// Presentational-only redesign of the MH "AI readout" block - takes the same
// data ConsultationEdit.js already has, no new state/fetches of its own.
// Same logic as before (failure banner text by retry count, review-meta
// parsing, Evaluate/Re-evaluate gating, brace/quote stripping), just a
// card-grid "insights report" look instead of a pile of disabled textareas.
// `onChange` (the page's handleChange) makes the clinician's fields editable;
// receptionists get the read-only view.
export default function SessionInsightsPanel({ consultationData, generatingSummary, onGenerateSummary, isAdmin, isReceptionist = false, onChange }) {
  const edit = !isReceptionist && onChange ? onChange : undefined;
  const field = (name, placeholder) => ({ name, rawValue: consultationData[name] || "", onChange: edit, placeholder });
  const hasSummary = Boolean(consultationData.summary && consultationData.summary.trim() !== "");
  // Hidden per product request (kept for easy re-enable, same convention as
  // ConsultationNew.js's SHOW_SESSION_DETAILS) - was: !isReceptionist &&
  // (!hasSummary || isAdmin).
  const showEvaluateButton = false;

  const mainthemes = (consultationData.mainthemes || "").replaceAll("{", "").replaceAll("}", "").replaceAll('"', "");
  const interventions = (consultationData.interventions || "").replaceAll("{", "").replaceAll("}", "").replaceAll('"', "");

  let reviewMeta = null;
  if (consultationData.supervisor_evaluation) {
    try {
      const evalData = typeof consultationData.supervisor_evaluation === "string"
        ? JSON.parse(consultationData.supervisor_evaluation)
        : consultationData.supervisor_evaluation;
      reviewMeta = evalData?.review_meta || null;
    } catch (e) { /* leave reviewMeta null */ }
  }

  return (
    <div className="space-y-4">
      {/* Summary + Evaluate action */}
      <Card className="border-primary-100 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary-50 border border-primary-100 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-primary-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Session Summary</h3>
            </div>
            {showEvaluateButton && (
              <button
                type="button"
                onClick={onGenerateSummary}
                disabled={generatingSummary}
                className={`${hasSummary ? "bg-orange-500 hover:bg-orange-600" : "bg-primary-600 hover:bg-primary-700"
                  } text-white px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm hover:shadow-md transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed shrink-0`}
              >
                {generatingSummary ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Evaluating...
                  </>
                ) : hasSummary ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5" />
                    Re-evaluate
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5" />
                    Evaluate
                  </>
                )}
              </button>
            )}
          </div>

          {consultationData.summary_generation_status === "failed" && (
            <div className="flex items-start gap-2 mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                {consultationData.summary_generation_retry_count >= 99
                  ? "Report generation failed — the transcript appears to be empty or too short to evaluate."
                  : consultationData.summary_generation_retry_count < 3
                    ? "Report generation failed. It will retry automatically in a few minutes."
                    : "Report generation failed after multiple attempts. Please check the transcript and try again."}
              </span>
            </div>
          )}

          {edit ? (
            <textarea id="note_summary" name="summary" value={consultationData.summary || ""} onChange={edit} aria-label="Session summary"
              placeholder="What was worked on, what came out of it and where things stand. Later sessions and supervision read this first."
              className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white min-h-[110px] focus:outline-none focus:ring-2 focus:ring-primary-200" />
          ) : hasSummary ? (
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50/70 rounded-lg p-3.5 border border-gray-100">
              {consultationData.summary}
            </p>
          ) : (
            <p className="text-xs text-gray-400 italic bg-gray-50/70 rounded-lg p-3.5 border border-dashed border-gray-200">
              No session summary yet.
            </p>
          )}

          {reviewMeta && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h4 className="text-[11px] font-bold text-gray-500 mb-2.5 tracking-wide uppercase">AI Quality Metrics</h4>
              <div className="flex flex-wrap gap-4 items-center">
                <ConfidenceIndicator
                  confidenceScore={reviewMeta.confidence_score}
                  confidenceLevel={reviewMeta.confidence_level}
                  size="md"
                  showLabel={true}
                />
                <ManualReviewBadge meta={reviewMeta} size="md" showReasons={true} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Insight cards, regrouped under SOAP headers - same cards/data as
          before, just organized the way a SOAP note reads: what the patient
          reported, what you observed, your clinical synthesis, then plan. */}
      <div className="space-y-5">
        <SoapGroup label="Subjective — What the Patient Reported" tint="bg-sky-50/20 border-sky-200/60">
          <InsightCard icon={MessageSquare} label="Main Themes" value={mainthemes} span {...field("mainthemes", "Central topics the patient raised or discussed")} />
        </SoapGroup>

        <SoapGroup label="Objective — What You Observed" tint="bg-teal-50/20 border-teal-200/60">
          <InsightCard icon={Heart} label="Mood & Affect" value={consultationData.mood} {...field("mood", "Describe patient's emotional state")} />
          <InsightCard icon={Activity} label="Resistance or Engagement" value={consultationData.resistanceorengagement} {...field("resistanceorengagement", "Note moments of resistance or engagement")} />
        </SoapGroup>

        <SoapGroup label="Assessment & Plan — Your Clinical Analysis" tint="bg-indigo-50/20 border-indigo-200/60">
          <InsightCard icon={Brain} label="Techniques Used" value={interventions} {...field("interventions", "Techniques used during session")} />
          <InsightCard icon={CheckCircle2} label="Effectiveness" value={consultationData.interventionseffectiveness} {...field("interventionseffectiveness", "Patient's response")} />
          <InsightCard icon={Lightbulb} label="Updated Hypothesis" value={consultationData.updatedhypothesis} span {...field("updatedhypothesis", "Your current understanding")} />
          <InsightCard icon={Target} label="Session Outcome" value={consultationData.sessionoutcome} {...field("sessionoutcome", "Summary of what was achieved")} />
          {/* The clinician's private plan for next session, beside this session's outcome. */}
          <InsightCard icon={ArrowRight} label="Next Session's Plan (private)" value={consultationData.nextplan} {...field("nextplan", "e.g. Continue CBT, introduce 5-4-3-2-1 grounding next session, review thought-record homework")} />
          {/* AI feedback on the session, for the clinician: read-only. */}
          {SHOW_AI_FEEDBACK && <InsightCard icon={Sparkles} label="What Went Well (AI feedback)" value={consultationData.what_went_well} />}
        </SoapGroup>
      </div>
    </div>
  );
}

// Thin labeled, tinted wrapper around a group of InsightCards - matches
// ConsultationNew.js's own Subjective/Objective/Assessment/Plan section
// boxes exactly, so the read-only AI summary here and the editable form
// there read as the same visual language.
function SoapGroup({ label, tint, children }) {
  return (
    <div className={`p-4 rounded-xl border ${tint}`}>
      <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">{label}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
